import { KanbanCard, KanbanRegularColumn } from './HBKanban.types';

/**
 * Finds a card by ID in the items structure
 * @param items - The items structure mapping column IDs to arrays of cards
 * @param cardId - The ID of the card to find
 * @returns The card if found, undefined otherwise
 */
export function getCardById<TItemData = any>(
    items: Record<string, KanbanCard<TItemData>[]>,
    cardId: string
): KanbanCard<TItemData> | undefined {
    return Object.values(items)
        .flat()
        .find((card: KanbanCard<TItemData>) => card.id === cardId);
}

/**
 * Finds which column contains a card with the given ID
 * @param items - The items structure mapping column IDs to arrays of cards
 * @param cardId - The ID of the card to find
 * @returns The column ID if found, null otherwise
 */
export function findCardColumn<TItemData = any>(
    items: Record<string, KanbanCard<TItemData>[]>,
    cardId: string
): string | null {
    for (const [columnId, columnCards] of Object.entries(items)) {
        if (columnCards.some(card => card.id === cardId)) {
            return columnId;
        }
    }
    return null;
}

/**
 * Move configuration for a single card
 */
export interface CardMove {
    cardId: string;
    sourceColumnId: string;
    targetColumnId: string;
}

/**
 * Moves multiple cards to their target columns
 * Each card can move to a different target column.
 * Cards are prepended to their target columns.
 * @param items - The items structure mapping column IDs to arrays of cards
 * @param moves - Array of move configurations, each specifying cardId, sourceColumnId, and targetColumnId
 * @returns A new items structure with all cards moved
 */
export function moveCards<TItemData = any>(
    items: Record<string, KanbanCard<TItemData>[]>,
    moves: CardMove[]
): Record<string, KanbanCard<TItemData>[]> {
    const newItems = { ...items };

    for (const move of moves) {
        const { cardId, sourceColumnId, targetColumnId } = move;

        if (sourceColumnId === targetColumnId) {
            continue;
        }

        const card = getCardById(newItems, cardId);
        if (!card) {
            continue;
        }

        if (!newItems[targetColumnId]) {
            newItems[targetColumnId] = [];
        }

        if (sourceColumnId && newItems[sourceColumnId]) {
            newItems[sourceColumnId] = newItems[sourceColumnId].filter(
                existingCard => existingCard.id !== cardId
            );
        }

        newItems[targetColumnId] = [card, ...newItems[targetColumnId]];
    }

    return newItems;
}

/**
 * Moves a single card to a target column
 * Convenience wrapper around moveCards
 * @param items - The items structure mapping column IDs to arrays of cards
 * @param cardId - The ID of the card to move
 * @param sourceColumnId - The ID of the source column
 * @param targetColumnId - The ID of the target column
 * @returns A new items structure with the card moved
 */
export function moveCard<TItemData = any>(
    items: Record<string, KanbanCard<TItemData>[]>,
    cardId: string,
    sourceColumnId: string,
    targetColumnId: string
): Record<string, KanbanCard<TItemData>[]> {
    return moveCards(items, [{ cardId, sourceColumnId, targetColumnId }]);
}

/**
 * Removes a card from the items structure
 * @param items - The items structure mapping column IDs to arrays of cards
 * @param cardId - The ID of the card to remove
 * @param columnId - Optional column ID. If provided, removes from that column. If not provided, finds the column automatically
 * @returns A new items structure with the card removed
 */
export function removeCard<TItemData = any>(
    items: Record<string, KanbanCard<TItemData>[]>,
    cardId: string,
    columnId?: string
): Record<string, KanbanCard<TItemData>[]> {
    const targetColumnId = columnId || findCardColumn(items, cardId);

    if (!targetColumnId) {
        // Card not found, return unchanged (but still create new object for React)
        return { ...items };
    }

    if (!items[targetColumnId]) {
        // Column doesn't exist, return unchanged (but still create new object for React)
        return { ...items };
    }

    // Create new items object with all columns
    const newItems: Record<string, KanbanCard<TItemData>[]> = {};

    // Copy all columns, filtering the target column
    for (const [colId, columnCards] of Object.entries(items)) {
        if (colId === targetColumnId) {
            // Remove the card from this column
            newItems[colId] = columnCards.filter(card => card.id !== cardId);
        } else {
            // Keep other columns as-is (but create new array reference)
            newItems[colId] = [...columnCards];
        }
    }

    return newItems;
}

/**
 * Restores a card to its original position in the items structure
 * @param items - The items structure mapping column IDs to arrays of cards
 * @param card - The card to restore
 * @param columnId - The column ID where the card should be restored
 * @param originalIndex - The original index position in the column
 * @returns A new items structure with the card restored
 */
export function restoreCard<TItemData = any>(
    items: Record<string, KanbanCard<TItemData>[]>,
    card: KanbanCard<TItemData>,
    columnId: string,
    originalIndex: number
): Record<string, KanbanCard<TItemData>[]> {
    // Create new items object with all columns
    const newItems: Record<string, KanbanCard<TItemData>[]> = {};

    // Ensure the target column exists
    if (!newItems[columnId]) {
        newItems[columnId] = [];
    }

    // Copy all columns and restore the card
    for (const [colId, columnCards] of Object.entries(items)) {
        if (colId === columnId) {
            // Restore card at original index position
            // If new items were loaded during async operation, they're appended to the end,
            // so we can safely restore at the original index (it will be <= current length)
            const newColumnCards = [...columnCards];
            // Clamp index to array length to handle edge cases gracefully
            const insertIndex = Math.min(originalIndex, newColumnCards.length);
            newColumnCards.splice(insertIndex, 0, card);
            newItems[colId] = newColumnCards;
        } else {
            // Keep other columns as-is (but create new array reference)
            newItems[colId] = [...columnCards];
        }
    }

    // If column didn't exist, create it with the card
    if (!items[columnId]) {
        newItems[columnId] = [card];
    }

    return newItems;
}

/**
 * Information about a card's original position
 */
export interface CardOriginalPosition {
    sourceColumnId: string;
    originalIndex: number;
}

/**
 * Reverts cards back to their original source columns and index positions.
 * Handles partial bulk move reverts by preserving relative order - if some cards
 * from a consecutive group were moved successfully and others are being reverted,
 * they maintain their correct positions relative to cards that remained.
 *
 * @param items - The current items structure mapping column IDs to arrays of cards
 * @param cardIdsToRevert - Array of card IDs to revert
 * @param originalPositions - Map of cardId -> { sourceColumnId, originalIndex } for cards being reverted
 * @param allOriginalIndices - Map of cardId -> originalIndex for ALL cards (needed to handle partial bulk moves)
 * @returns A new items structure with cards reverted to their original positions
 */
export function revertCards<TItemData = any>(
    items: Record<string, KanbanCard<TItemData>[]>,
    cardIdsToRevert: string[],
    originalPositions: Map<string, CardOriginalPosition>,
    allOriginalIndices: Map<string, number>
): Record<string, KanbanCard<TItemData>[]> {
    const newItems = { ...items };

    const cardsBySourceColumn = new Map<
        string,
        Array<{ card: KanbanCard<TItemData>; originalIndex: number }>
    >();

    cardIdsToRevert.forEach(cardId => {
        const card = getCardById(newItems, cardId);
        if (!card) {
            return;
        }

        const originalPos = originalPositions.get(cardId);
        if (!originalPos) {
            return;
        }

        Object.keys(newItems).forEach(columnId => {
            if (newItems[columnId]) {
                newItems[columnId] = newItems[columnId].filter(
                    c => c.id !== cardId
                );
            }
        });

        if (!cardsBySourceColumn.has(originalPos.sourceColumnId)) {
            cardsBySourceColumn.set(originalPos.sourceColumnId, []);
        }
        cardsBySourceColumn.get(originalPos.sourceColumnId)!.push({
            card,
            originalIndex: originalPos.originalIndex
        });
    });

    cardsBySourceColumn.forEach((cardsToRevert, sourceColumnId) => {
        if (!newItems[sourceColumnId]) {
            newItems[sourceColumnId] = [];
        }

        // Separate cards into those with original indices and those without
        const cardsWithOriginalIndex: Array<{
            card: KanbanCard<TItemData>;
            originalIndex: number;
        }> = [];
        const cardsWithoutOriginalIndex: KanbanCard<TItemData>[] = [];

        newItems[sourceColumnId].forEach(card => {
            const originalIndex = allOriginalIndices.get(card.id);
            if (originalIndex !== undefined) {
                cardsWithOriginalIndex.push({ card, originalIndex });
            } else {
                // Preserve cards without original indices in their current positions
                cardsWithoutOriginalIndex.push(card);
            }
        });

        // Add reverted cards to the list with original indices
        cardsWithOriginalIndex.push(...cardsToRevert);
        // Sort by original index to restore correct order
        cardsWithOriginalIndex.sort(
            (a, b) => a.originalIndex - b.originalIndex
        );

        // Rebuild column: cards with original indices (in correct order) + cards without (at the end)
        newItems[sourceColumnId] = [
            ...cardsWithOriginalIndex.map(item => item.card),
            ...cardsWithoutOriginalIndex
        ];
    });

    return newItems;
}

/**
 * Calculates column counts from columns and items
 * @param columns - Array of regular columns (non-dividers)
 * @param items - The items structure mapping column IDs to arrays of cards
 * @returns Record mapping column IDs to their counts
 */
export function calculateColumnCounts<TItemData = any>(
    columns: KanbanRegularColumn[],
    items: Record<string, KanbanCard<TItemData>[]>
): Record<string, number> {
    return columns.reduce((acc, column) => {
        acc[column.id] = column.count ?? items[column.id]?.length ?? 0;
        return acc;
    }, {} as Record<string, number>);
}
